import {
    AlgoliaWine,
    getCountry,
    getDescription,
    getName,
    getRegion,
    getSuitableFood
} from "../winepicker/winePickerModel";
import {
    CoverPage,
    DocumentDrinkSizes,
    DrinkSize,
    ImageItem,
    isTitleItem,
    isWineItem,
    MarginItem,
    MenuDocument,
    MenuFormat,
    MenuItem,
    MenuPage,
    PageBreakItem,
    PageTitleItem,
    TitleItem,
    WineItem
} from "./MenuDocumentModel";
import {add, always, assoc, assocPath, evolve, fromPairs, identity, isEmpty, last, zip} from "ramda";
import {EditorItemKey, menuEditorActions} from "./editorSlice";
import {addAll} from "lib/util";
import {getPredefinedImageCollection} from "./sidebar/image/imageCollections";
import {Language} from "../i18n/translations";
import {useAppDispatch} from "../../lib/redux/hooks";
import { i18nSelectors } from "features/i18n/i18nSlice";
import { store } from "lib/redux/store";
import { createDocumentHandler } from "./documentHandlerUtils";

export function createEmptyDocument(language: Language): MenuDocument {
    return {
        type: 'wine',
        language: language,
        format: 'a4',
        coverPage: null,
        printMode: "singlepages",
        displayFooter: false,
        showAppellation: true,
        showYear: true,
        degu: false,
        pages: [],
        sizes: ["1dl", "2dl", "5dl", "Flasche"]
    };
}

export function createEmptyLeafletDocument(language: Language): MenuDocument {
    return {
        ...createEmptyDocument(language),
        type: 'leaflet',
        format: 'a6'
    };
}

export function createEmptyCoverPage(): CoverPage {
    return {
        title: "Restaurantname",
        coverImageUrl: getPredefinedImageCollection("cover")[0],
        logoImageUrl: null
    };
}

export function createTitle(title?: string, sizes?: DrinkSize[]): TitleItem {
    return {
        type: 'title',
        title: title ?? "Überschrift",
        sizes: sizes ?? []
    };
}

export function createPageTitle(title?: string): PageTitleItem {
    return {
        type: 'pageTitle',
        title: title ?? "Seitenüberschrift"
    };
}

export function createMargin(height?: number): MarginItem {
    return {
        type: 'margin',
        height: height ?? 5
    };
}

export function createImage(url?: string, height?: number): ImageItem {
    return {
        type: 'image',
        url: url ?? getPredefinedImageCollection("filler")[0],
        height: height ?? 15
    };
}

export function createPageBreak(): PageBreakItem {
    return {
        type: 'pagebreak'
    };
}

export function createEmptyWine(): WineItem {
    return {
        type: 'wine',
        external: true,
        detailed: false,
        showDescription: false,
        country: null,
        objectID: null,
        img_url: null,
        text: '',
        region: '',
        grapes: '',
        producer: '',
        name: 'Neuer Wein',
        suitableFood: '',
        year: 2019,
        size: 75,
        sweetness: 1,
        taste: 1,
        badges: [],
        prices: [null, null, null, null],
        displayPrices: [],
        purchasePricePerBottle: null
    };
}

export function algoliaWineToEditorWine(language: Language, algoliaWine: AlgoliaWine): WineItem {
    return {
        type: 'wine',
        external: false,
        detailed: true,
        showDescription: true,
        objectID: algoliaWine.objectID,
        img_url: algoliaWine.img_url,
        name: getName(language, algoliaWine),
        year: algoliaWine.year,
        size: algoliaWine.bottle_size,
        country: getCountry(language, algoliaWine),
        region: getRegion(language, algoliaWine) ?? "",
        producer: algoliaWine.producer ?? "",
        grapes: algoliaWine.grapes.text ?? "",
        text: getDescription(language, algoliaWine) ?? "",
        taste: algoliaWine.taste,
        sweetness: algoliaWine.sweetness,
        suitableFood: getSuitableFood(language, algoliaWine)?.filter(f => !isEmpty(f))?.join(", "),
        badges: [],
        prices: [null, null, null, null],
        displayPrices: [],
        purchasePricePerBottle: algoliaWine.regular_price_per_bottle ?? algoliaWine.sale_price_per_bottle ?? null,
        appellation: algoliaWine.appellation
    };
}

export interface EditorItem<T extends MenuItem> {
    itemKey: EditorItemKey
    menuItem: T
}
export type EditorPage = { key: number, page: MenuPage }

export function getLastItemKey(pages: MenuPage[]): EditorItemKey | null {
    if (pages.length === 0 || pages[0].length === 0) {
        return null;
    }

    const lastPage = pages.length - 1;
    const lastItem = pages[lastPage].length - 1;
    return {
        page: lastPage,
        item: lastItem
    };
}

export function compareItemKeys(e1: EditorItemKey, e2: EditorItemKey) {
    if (e1 === e2) {
        return 0;
    }

    const pageDiff = e1.page - e2.page;
    return pageDiff === 0 ?
        Math.abs(e1.item - e2.item) :
        Math.abs(pageDiff);
}

export function toKeyedEditorItemList(pages: MenuPage[]): EditorItem<MenuItem>[] {
    return pages.flatMap((page, pageIndex) =>
        page.map((item, itemIndex) => ({
            itemKey: {page: pageIndex, item: itemIndex},
            menuItem: item
        })));
}

export function toFlatItemList(pages: MenuPage[]): MenuItem[] {
    return pages.flatMap<MenuItem>(identity);
}

export function updateDrinkSizes(document: MenuDocument): MenuPage[] {
    const winesByTitleKey = getWinesByTitleKey(document.pages);
    const sizeGroups: SizeGroup[] = winesByTitleKey.map(g => {
        const sizes = getSizes(document.sizes, g.wines.map(w => w.menuItem));
        return assoc('groupSizes', sizes, g)
    });

    const updatedTitles = updateTitleDrinkSizes(sizeGroups, document.pages);
    return updateWineDisplaySizes(document.sizes, sizeGroups, updatedTitles);
}

interface SizeGroup extends TitleWineGroup {
    groupSizes: DrinkSize[]
}

function updateTitleDrinkSizes(sizeGroups: SizeGroup[], pages: MenuPage[]): MenuPage[] {
    return sizeGroups.reduce((result, sizeGroup) => {
        const titleKey = sizeGroup.titleKey;
        return assocPath([titleKey.page, titleKey.item, 'sizes'], sizeGroup.groupSizes, result);
    }, pages);
}

function updateWineDisplaySizes(documentSizes: DocumentDrinkSizes, sizeGroups: SizeGroup[], pages: MenuPage[]): MenuPage[] {
    const wineSizes = sizeGroups.flatMap(g => g.wines.map(w => ({groupSizes: g.groupSizes, wine: w})));
    return wineSizes.reduce((result, {wine, groupSizes}) => {
        const wineSizePrices = getWineSizePrices(documentSizes, wine.menuItem);
        const displayPrices: (number | null)[] = groupSizes.map(s => wineSizePrices[s] ?? null);
        const wineKey = wine.itemKey;
        return assocPath([wineKey.page, wineKey.item, 'displayPrices'], displayPrices, result);
    }, pages);
}

type WineSizePrices = Partial<Record<DrinkSize, number>>

function getWineSizePrices(documentSizes: DocumentDrinkSizes, wineItem: WineItem): WineSizePrices {
    const nonNullPairs = zip(documentSizes, wineItem.prices)
        .filter(([, price]) => price != null) as [DrinkSize, number][];
    return fromPairs(nonNullPairs) as WineSizePrices;
}

function getSizes(documentSizes: DocumentDrinkSizes, wines: WineItem[]): DrinkSize[] {
    const sizeSet = wines.reduce((result, wine) => addAll(getWineSizes(documentSizes, wine), result), new Set<DrinkSize>());
    return documentSizes.filter(s => sizeSet.has(s));
}

function getWineSizes(documentSizes: DocumentDrinkSizes, wine: WineItem): DrinkSize[] {
    return documentSizes.filter((val, index) => wine.prices[index] != null);
}

interface TitleWineGroup {
    titleKey: EditorItemKey
    wines: EditorItem<WineItem>[]
}

function getWinesByTitleKey(pages: MenuPage[]): TitleWineGroup[] {
    const flatList = toKeyedEditorItemList(pages);
    const groups = flatList.reduce<(TitleWineGroup | null)[]>((accum, item) => {
        if (isTitleItem(item.menuItem)) {
            accum.push({titleKey: item.itemKey, wines: []});
        } else if (isWineItem(item.menuItem)) {
            const lastGroup = last(accum);
            if (lastGroup != null) {
                lastGroup.wines.push(item as EditorItem<WineItem>);
            }
        }
        return accum;
    }, []);

    return groups.filter(g => g != null) as TitleWineGroup[];
}

export function refocusAfterItemRedistribution(document: MenuDocument, currentFocus: EditorItemKey | null): EditorItemKey | null {
    if (currentFocus == null
        || document.pages.length === 0
    ) {
        return null;
    }

    if (isExistingItemKey(document, currentFocus)) {
        return currentFocus;
    }

    if (currentFocus.page >= document.pages.length) {
        return evolve({
            page: add(-1),
            item: always(document.pages[currentFocus.page - 1].length - 1)
        }, currentFocus);
    }

    let currentPos = currentFocus;
    let pageLength = document.pages[currentPos.page].length;
    let offset = currentPos.item - pageLength;
    while (offset >= 0) {
        currentPos = evolve({page: add(1), item: always(offset)}, currentPos);
        pageLength = document.pages[currentPos.page].length;
        offset = currentPos.item - pageLength;
    }

    return currentPos;
}

export function isExistingItemKey(document: MenuDocument, key: EditorItemKey) {
    return document.pages[key.page]?.[key.item] != null;
}

export function redistributeItemsToFitPages(document: MenuDocument): MenuPage[] {
    if (document.pages.length === 0) {
        return [];
    } 
    const handler = createDocumentHandler(document);

    let result: MenuPage[] = [[]];
    const flatItemList = document.pages.flatMap<MenuItem>(identity);
    for (const item of flatItemList) {
        const currentPage = last(result)!;

        if (handler.shouldBreakPageAfterItem(item)) {
            currentPage.push(item);
            result.push([]);
            continue;
        }

        if (fitsIntoPage(currentPage, document.format, item)) {
            currentPage.push(item);
        } else {
            result.push([item]);
        }
    }

    if (last(result)?.length === 0) {
        result.pop();
    }

    return result;
}

export function fitsIntoPage(page: MenuPage, format: MenuFormat, item: MenuItem): boolean {
    const lastItem = last(page);
    if (lastItem != null && getItemHeightInMm(format, lastItem) === "rest") {
        return false;
    }

    const newItemHeight = getItemHeightInMm(format, item);
    if (newItemHeight === 'rest') {
        return true;
    }

    // there should be no 'rest' heights because of previous check
    const currentItemHeights = page.map(item => getItemHeightInMm(format, item)) as number[];
    const totalNewItemHeight = currentItemHeights.reduce(add, 0) + newItemHeight;
    const maxContentHeight = getContentHeightInMm(format);
    return totalNewItemHeight <= maxContentHeight;
}

function getContentHeightInMm(format: MenuFormat): number {
    switch (format) {
        case "a4":
        case "a4-half":
            return 257; // 297mm - 2 * 20mm
        case "a5":
            return 190; // 210mm - 2 * 10mm
        case "a6":
            return 125; // 135mm - 2 * 5mm;
        case "custom":
            return 0
    }
}

export function getItemHeightInMm(format: MenuFormat, item: MenuItem): number | 'rest' {
    switch (item.type) {
        case "wine":
            return (getWineHeightMm(format, item as WineItem) || 0) + (getWineMarginBottomMm(format) || 0);
        case "pageTitle": return 5.5 + 5;
        case "title": return 7 + 3.5;
        case "margin": return (item as MarginItem).height;
        case "image": return (item as ImageItem).height;
        case "pagebreak": 
        case "label":
            return "rest";
    }
}

export function getWineItemShowAppellation(wineItem: WineItem, document: MenuDocument): boolean {
    return Boolean(wineItem.showAppellation ?? document.showAppellation);
}

export function getShowWineBadgesOrFood(wine: WineItem): boolean {
    return getShowWineBadges(wine) || getShowWineFood(wine);
}

export function getShowWineBadges(wine: WineItem): boolean {
    return !!wine.badges && wine.badges.length>0;
}

export function getShowWineFood(wine: WineItem): boolean {
    return (
        wine.showSuitableFood === undefined
        || wine.showSuitableFood === null
        || wine.showSuitableFood === true
    )
    && !!wine.suitableFood;
}

export function getWineBadgesHeight(format: MenuFormat, wine: WineItem): number {
    return getShowWineBadgesOrFood(wine)?
        (format==="a4"?7:6)
        :0;
}

export function getWineHeightMm(format: MenuFormat, wine: WineItem): number|undefined {
    let base: number;
    switch (format) {
        case "a4":
            base = wine.detailed ? 28 : 16;
            break;
        case "a5":
            base = wine.detailed ? 25 : 13;
            break;
        case "a4-half":
            base = wine.detailed ? 27 : 14;
            break;
        case "a6":
            return 125; // 135mm - 5mm*2
        default:
            return undefined;
    }
    const badges = getWineBadgesHeight(format, wine);
    return base + badges;
}

export function getWineMarginBottomMm(format: MenuFormat): number|undefined {
    switch (format) {
        case "a4":
            return 3;
        case "a5":
            return 2;
        case "a4-half":
            return 3;
        default:
            return undefined;
    }
}

export function getMmCssValue(mmValue: number|undefined, defaultValue: string = "auto"): string {
    if (mmValue !== undefined) return `${mmValue}mm`;
    return defaultValue;
}

export function getShowTasteSweetness(wine: WineItem): boolean {
    return wine.showTasteSweetness == null || wine.showTasteSweetness;
}

export function getWineItemShowYear(wineItem: WineItem, document: MenuDocument): boolean {
    return wineItem.showYear??document.showYear??true;
}

export function useUpdateProperty<T extends MenuItem>(editorItem: EditorItem<T>): 
    <K extends Extract<keyof T, String>>(property: K, value: T[K])=>void 
{
    const itemKey = editorItem.itemKey;
    const menuItem = editorItem.menuItem;

    const dispatch = useAppDispatch();
    function updateProperty<K extends Extract<keyof T, String>>(property: K, value: T[K]) {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc<T[K], T, K>(property, value, menuItem) as unknown as T
        }));
    }
    return updateProperty;
}

export function nextEditorItemKey(document: MenuDocument, itemKey: EditorItemKey, offset: number): EditorItemKey {
    const pages = document.pages,
        pageOffset = Math.floor((itemKey.item + offset) / pages[itemKey.page].length),
        newPage = itemKey.page + pageOffset;

    if (newPage < 0) {
        return {page: 0, item: 0};
    }
    if (newPage > pages.length - 1) {
        return {page: pages.length-1, item: pages[pages.length-1].length-1};
    }
    const newItem = (
        pageOffset < 0 ? pages[newPage].length :
            pageOffset > 0 ? 1 : 0
    ) + (itemKey.item + offset) % pages[newPage].length;
    return {item: newItem, page: newPage};
}

export function isLeafletDocument(document: MenuDocument): boolean {
    return document.type === "leaflet";
}

export function getAppLanguage(): Language {
    return i18nSelectors.language(store.getState());
}
